import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { roomsApi, roomWsUrl, type RoomState } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';

type ConnStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

// Snapshot du gameStore diffusé par le host pour les joueurs non-host.
export type RemoteGameSnapshot = any;

// Clé localStorage pour persister le code de room (reconnexion après refresh)
const STORAGE_KEY = 'tchinquiz.online.room.v1';
// Action émise par un joueur non-host à appliquer chez le host.
export interface PlayerActionMsg {
  fromUserId: number;
  action: 'pick' | 'pass' | 'continue_round' | 'continue_set';
  payload?: any;
}

export const useOnlineStore = defineStore('online', () => {
  const room = ref<RoomState | null>(null);
  const status = ref<ConnStatus>('idle');
  const lastError = ref<string | null>(null);
  // État du jeu reçu du host (pour les joueurs non-host)
  const remoteGameState = ref<RemoteGameSnapshot | null>(null);
  // Dernière action reçue d'un joueur (à appliquer côté host) — bus simple
  const incomingAction = ref<PlayerActionMsg | null>(null);
  let ws: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let manualClose = false;

  const authStore = useAuthStore();
  const code = computed(() => room.value?.code ?? null);
  const isHost = computed(() => {
    const r = room.value;
    if (!r) return false;
    return r.host_user_id === authStore.user?.id;
  });
  const players = computed(() => room.value?.players ?? []);

  function setRoom(r: RoomState) {
    room.value = r;
    try { localStorage.setItem(STORAGE_KEY, r.code); } catch {}
  }
  function clear() {
    room.value = null;
    remoteGameState.value = null;
    incomingAction.value = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }

  /** Tente une reconnexion à la dernière room connue (après refresh). */
  async function tryResume(): Promise<boolean> {
    const code = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
    if (!code) return false;
    try {
      const r = await roomsApi.get(code);
      // Room toujours valide côté serveur (player encore dedans). On reprend la WS.
      setRoom(r);
      connectWs(r.code);
      return true;
    } catch {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      return false;
    }
  }

  async function createRoom(
    transport: RoomState['transport'],
    settings: Record<string, any> = {},
    opts: { is_public?: boolean; room_name?: string } = {},
  ) {
    lastError.value = null;
    const r = await roomsApi.create(transport, settings, opts);
    setRoom(r);
    connectWs(r.code);
    return r;
  }

  async function joinRoom(joinCode: string) {
    lastError.value = null;
    const r = await roomsApi.join(joinCode);
    setRoom(r);
    connectWs(r.code);
    return r;
  }

  function connectWs(roomCode: string) {
    closeWs(true);
    manualClose = false;
    status.value = 'connecting';
    const url = roomWsUrl(roomCode);
    ws = new WebSocket(url);

    ws.onopen = () => {
      status.value = 'open';
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        // eslint-disable-next-line no-console
        console.log('[WS recv]', msg.type, msg);
        handleMessage(msg);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[WS recv] parse error', e);
      }
    };
    ws.onerror = () => {
      lastError.value = 'ws_error';
      status.value = 'error';
    };
    ws.onclose = () => {
      ws = null;
      if (manualClose) {
        status.value = 'closed';
        return;
      }
      status.value = 'closed';
      // reconnexion automatique simple : 1 essai après 2s tant qu'on a une room
      if (room.value && reconnectTimer === null) {
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          if (room.value) connectWs(room.value.code);
        }, 2000);
      }
    };
  }

  function send(msg: Record<string, any>) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        const json = JSON.stringify(msg);
        // eslint-disable-next-line no-console
        console.log('[WS send]', msg.type, json.length, 'bytes');
        ws.send(json);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[WS send] serialize failed', msg.type, e);
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[WS send] dropped — WS not open. State:', ws?.readyState);
    }
  }

  function startGame(settings?: Record<string, any>) {
    send({ type: 'start_game', settings });
  }

  function endGame() {
    send({ type: 'end_game' });
  }

  /** Le host diffuse un snapshot de son gameStore aux autres joueurs. */
  function sendHostState(snapshot: any) {
    send({ type: 'host_state', state: snapshot });
  }

  /** Un joueur non-host envoie une action au host. */
  function sendPlayerAction(action: PlayerActionMsg['action'], payload?: any) {
    send({ type: 'player_action', action, payload });
  }

  function leave() {
    closeWs(true);
    clear();
  }

  function closeWs(manual: boolean) {
    manualClose = manual;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) {
      try { ws.close(); } catch {}
      ws = null;
    }
  }

  function handleMessage(msg: any) {
    switch (msg.type) {
      case 'room_state':
        if (msg.room) setRoom(msg.room);
        break;
      case 'player_joined':
        if (room.value && msg.player) {
          const existing = room.value.players.find(p => p.user_id === msg.player.user_id);
          if (existing) Object.assign(existing, msg.player);
          else room.value.players.push(msg.player);
        }
        break;
      case 'player_left':
        if (room.value && msg.user_id !== undefined) {
          const p = room.value.players.find(x => x.user_id === msg.user_id);
          if (p) p.connected = false;
        }
        break;
      case 'game_started':
        if (msg.room) setRoom(msg.room);
        break;
      case 'game_ended':
        if (msg.room) setRoom(msg.room);
        break;
      case 'game_state':
        // Snapshot reçu du host (côté joueur non-host)
        if (msg.state !== undefined) remoteGameState.value = msg.state;
        break;
      case 'player_action':
        // Action d'un joueur reçue côté host (à dispatcher dans gameStore)
        incomingAction.value = {
          fromUserId: msg.from_user_id,
          action: msg.action,
          payload: msg.payload,
        };
        break;
      case 'error':
        lastError.value = msg.code ?? 'unknown_error';
        break;
      case 'pong':
        break;
    }
  }

  return {
    room, status, lastError,
    remoteGameState, incomingAction,
    code, isHost, players,
    createRoom, joinRoom, startGame, endGame, leave, send,
    sendHostState, sendPlayerAction,
    tryResume,
  };
});
